import { db } from './db';
import { apiClient, ApiError } from './api-client';
import { SYNC_CONFIG } from '@/config/constants';
import { eventBus } from '@/lib/event-bus';
import type { SyncQueueItem, MediaQueueItem, SyncEngineStatus } from '@/types/sync';

/**
 * Calculate exponential backoff delay with jitter.
 */
function getBackoffDelay(retryCount: number): number {
  const baseDelay = SYNC_CONFIG.BASE_RETRY_DELAY;
  const exponential = Math.min(
    baseDelay * Math.pow(2, retryCount),
    SYNC_CONFIG.MAX_RETRY_DELAY,
  );
  // Add jitter (0-25% of the delay)
  const jitter = exponential * Math.random() * 0.25;
  return exponential + jitter;
}

/**
 * The SyncEngine processes queued mutations and media uploads
 * in FIFO order when the device is online.
 *
 * Conflict resolution: last-write-wins using timestamps.
 * Failed items are retried with exponential backoff up to MAX_RETRY_ATTEMPTS.
 */
export class SyncEngine {
  private isSyncing = false;
  private isStarted = false;
  private syncTimer: ReturnType<typeof setTimeout> | null = null;
  private _onStatusChange: ((status: SyncEngineStatus) => void) | null = null;

  /** Register a callback for status changes. */
  onStatusChange(callback: (status: SyncEngineStatus) => void): void {
    this._onStatusChange = callback;
  }

  /** Get current sync engine status. */
  async getStatus(): Promise<SyncEngineStatus> {
    const pendingSyncCount = await db.syncQueue
      .where('status')
      .anyOf('pending', 'syncing')
      .count();

    const pendingMediaCount = await db.mediaQueue
      .where('status')
      .anyOf('pending', 'syncing')
      .count();

    const failedSyncCount = await db.syncQueue
      .where('status')
      .equals('failed')
      .count();

    const failedMediaCount = await db.mediaQueue
      .where('status')
      .equals('failed')
      .count();

    return {
      isOnline: navigator.onLine,
      isSyncing: this.isSyncing,
      pendingSyncCount,
      pendingMediaCount,
      failedCount: failedSyncCount + failedMediaCount,
      lastSyncAt: undefined, // Could persist this in a separate store
    };
  }

  /** Start the sync engine — begins processing queues. */
  start(): void {
    if (this.isStarted) return;
    this.isStarted = true;

    this.scheduleSync();

    // Listen for online/offline events
    window.addEventListener('online', this.handleOnline);
    window.addEventListener('offline', this.handleOffline);
  }

  /** Stop the sync engine. */
  stop(): void {
    this.isStarted = false;

    if (this.syncTimer) {
      clearTimeout(this.syncTimer);
      this.syncTimer = null;
    }
    window.removeEventListener('online', this.handleOnline);
    window.removeEventListener('offline', this.handleOffline);
  }

  /** Process all pending items in the sync queue (FIFO). */
  async processSync(): Promise<void> {
    if (this.isSyncing || !navigator.onLine) return;

    this.isSyncing = true;
    this.notifyStatusChange();

    try {
      // 1. Process media uploads first (photos and audio need to be uploaded
      //    before mutations that reference them)
      await this.processMediaQueue();

      // 2. Process mutation queue (FIFO order)
      await this.processSyncQueue();
    } catch (error) {
      console.error('[SyncEngine] Unexpected error during sync:', error);
    } finally {
      this.isSyncing = false;
      this.notifyStatusChange();
      this.scheduleSync();
    }
  }

  /** Retry all failed items by resetting their status to pending. */
  async retryFailed(): Promise<void> {
    await db.transaction('rw', db.syncQueue, db.mediaQueue, async () => {
      await db.syncQueue
        .where('status')
        .equals('failed')
        .modify({ status: 'pending', retryCount: 0 });

      await db.mediaQueue
        .where('status')
        .equals('failed')
        .modify({ status: 'pending', retryCount: 0 });
    });

    this.notifyStatusChange();

    // Trigger sync immediately
    if (navigator.onLine) {
      await this.processSync();
    }
  }

  // ---------------------------------------------------------------------------
  // Private
  // ---------------------------------------------------------------------------

  private handleOnline = (): void => {
    console.log('[SyncEngine] Device online — starting sync');
    this.processSync();
  };

  private handleOffline = (): void => {
    console.log('[SyncEngine] Device offline — pausing sync');
    this.notifyStatusChange();
  };

  private scheduleSync(): void {
    if (this.syncTimer) clearTimeout(this.syncTimer);
    this.syncTimer = setTimeout(() => {
      if (navigator.onLine) {
        this.processSync();
      }
    }, SYNC_CONFIG.MIN_SYNC_INTERVAL);
  }

  private async notifyStatusChange(): Promise<void> {
    if (this._onStatusChange) {
      const status = await this.getStatus();
      this._onStatusChange(status);
    }
  }

  /**
   * Process the media upload queue. Upload blobs to the server.
   */
  private async processMediaQueue(): Promise<void> {
    const pendingItems = await db.mediaQueue
      .where('status')
      .equals('pending')
      .sortBy('createdAt');

    const batch = pendingItems.slice(0, SYNC_CONFIG.BATCH_SIZE);

    for (const item of batch) {
      if (!navigator.onLine) break;
      await this.processMediaItem(item);
    }
  }

  private async processMediaItem(item: MediaQueueItem): Promise<void> {
    const itemId = item.id!;

    try {
      if (
        item.jobId.startsWith('local_') ||
        item.inspectionId?.startsWith('local_') ||
        item.uploadEndpoint.includes('/local_')
      ) {
        return;
      }

      // Mark as syncing
      await db.mediaQueue.update(itemId, { status: 'syncing' });

      // Upload the blob
      await apiClient.upload(
        item.uploadEndpoint,
        item.blob,
        item.fileName,
        item.additionalFields,
        item.fieldName || 'file',
      );

      // Success — remove from queue
      await db.mediaQueue.delete(itemId);
      this.emitMediaMutationEvent(item);
    } catch (error) {
      const newRetryCount = item.retryCount + 1;
      const shouldRetry = newRetryCount < SYNC_CONFIG.MAX_RETRY_ATTEMPTS;
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';

      // If it's a 4xx error (client error), don't retry
      const isClientError = error instanceof ApiError && error.status >= 400 && error.status < 500;

      await db.mediaQueue.update(itemId, {
        status: (!shouldRetry || isClientError) ? 'failed' : 'pending',
        retryCount: newRetryCount,
        lastError: errorMessage,
      });

      if (shouldRetry && !isClientError) {
        // Wait with backoff before next item
        await new Promise((r) => setTimeout(r, getBackoffDelay(newRetryCount)));
      }
    }
  }

  /**
   * Process the mutation sync queue. Send pending API calls in FIFO order.
   */
  private async processSyncQueue(): Promise<void> {
    const pendingItems = await db.syncQueue
      .where('status')
      .equals('pending')
      .sortBy('createdAt');

    const batch = pendingItems.slice(0, SYNC_CONFIG.BATCH_SIZE);

    for (const item of batch) {
      if (!navigator.onLine) break;
      await this.processSyncItem(item);
    }
  }

  private async processSyncItem(item: SyncQueueItem): Promise<void> {
    const itemId = item.id!;
    let mutationEventEmitted = false;

    try {
      // Mark as syncing
      await db.syncQueue.update(itemId, {
        status: 'syncing',
        lastAttemptAt: new Date().toISOString(),
      });

      // Execute the API call
      let response: Record<string, unknown> | undefined;
      switch (item.method) {
        case 'POST':
          response = await apiClient.post(item.endpoint, item.payload) as Record<string, unknown> | undefined;
          break;
        case 'PUT':
          await apiClient.put(item.endpoint, item.payload);
          break;
        case 'DELETE':
          await apiClient.delete(item.endpoint);
          break;
      }

      // Reconcile temp IDs: if a POST created a new entity with a server ID,
      // update the local IndexedDB record from the temp ID to the real one
      if (item.method === 'POST' && response && item.entityId?.startsWith('local_')) {
        const realId = (response as Record<string, unknown>).id as string | undefined;
        if (realId && realId !== item.entityId) {
          try {
            if (item.entityType === 'job') {
              const local = await db.jobs.get(item.entityId);
              if (local) {
                await db.jobs.delete(item.entityId);
                await db.jobs.put({ ...local, id: realId });
              }
              // Update child inspections that reference the temp job ID
              const childInspections = await db.inspections
                .where('jobId')
                .equals(item.entityId)
                .toArray();
              for (const insp of childInspections) {
                await db.inspections.update(insp.id!, { jobId: realId });
              }
              const childReports = await db.cachedReports
                .where('jobId')
                .equals(item.entityId)
                .toArray();
              for (const report of childReports) {
                await db.cachedReports.update(report.id!, { jobId: realId });
              }
              await this.rewriteQueuedReferences(item.entityId, realId, { jobId: realId });
              eventBus.emit('job:mutated', {
                id: realId,
                previousId: item.entityId,
                nextId: realId,
              });
              mutationEventEmitted = true;
            } else if (item.entityType === 'inspection') {
              const local = await db.inspections.get(item.entityId);
              if (local) {
                await db.inspections.delete(item.entityId);
                await db.inspections.put({ ...local, id: realId });
              }
              await this.rewriteQueuedReferences(item.entityId, realId, { inspectionId: realId });
              eventBus.emit('inspection:mutated', {
                id: realId,
                jobId: local?.jobId,
                previousId: item.entityId,
                nextId: realId,
              });
              mutationEventEmitted = true;
            }
          } catch (reconcileErr) {
            console.warn('[SyncEngine] Failed to reconcile temp ID:', reconcileErr);
          }
        }
      }

      // Success — remove from queue
      await db.syncQueue.delete(itemId);
      if (!mutationEventEmitted) {
        this.emitMutationEvent(item);
      }
    } catch (error) {
      const newRetryCount = item.retryCount + 1;
      const shouldRetry = newRetryCount < SYNC_CONFIG.MAX_RETRY_ATTEMPTS;
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';

      // If it's a 409 Conflict, apply last-write-wins: mark as synced (skip)
      if (error instanceof ApiError && error.status === 409) {
        console.warn(
          `[SyncEngine] Conflict for ${item.entityType}/${item.entityId} — last-write-wins, skipping`,
        );
        await db.syncQueue.delete(itemId);
        this.emitMutationEvent(item);
        return;
      }

      // If it's a 4xx error, don't retry
      const isClientError = error instanceof ApiError && error.status >= 400 && error.status < 500;

      await db.syncQueue.update(itemId, {
        status: (!shouldRetry || isClientError) ? 'failed' : 'pending',
        retryCount: newRetryCount,
        lastError: errorMessage,
        lastAttemptAt: new Date().toISOString(),
      });

      if (shouldRetry && !isClientError) {
        await new Promise((r) => setTimeout(r, getBackoffDelay(newRetryCount)));
      }
    }
  }

  private async rewriteQueuedReferences(
    previousId: string,
    nextId: string,
    overrides: { jobId?: string; inspectionId?: string } = {},
  ): Promise<void> {
    const pendingItems = await db.syncQueue.toArray();
    for (const pending of pendingItems) {
      const endpoint = pending.endpoint.includes(previousId)
        ? pending.endpoint.split(previousId).join(nextId)
        : pending.endpoint;
      const entityId = pending.entityId === previousId ? nextId : pending.entityId;
      if (endpoint !== pending.endpoint || entityId !== pending.entityId) {
        await db.syncQueue.update(pending.id!, { endpoint, entityId });
      }
    }

    const pendingMedia = await db.mediaQueue.toArray();
    for (const mediaItem of pendingMedia) {
      const uploadEndpoint = mediaItem.uploadEndpoint.includes(previousId)
        ? mediaItem.uploadEndpoint.split(previousId).join(nextId)
        : mediaItem.uploadEndpoint;
      const nextJobId = mediaItem.jobId === previousId ? overrides.jobId ?? nextId : mediaItem.jobId;
      const nextInspectionId = mediaItem.inspectionId === previousId
        ? overrides.inspectionId ?? nextId
        : mediaItem.inspectionId;

      if (
        uploadEndpoint !== mediaItem.uploadEndpoint ||
        nextJobId !== mediaItem.jobId ||
        nextInspectionId !== mediaItem.inspectionId
      ) {
        await db.mediaQueue.update(mediaItem.id!, {
          uploadEndpoint,
          jobId: nextJobId,
          inspectionId: nextInspectionId,
        });
      }
    }
  }

  private emitMutationEvent(item: SyncQueueItem): void {
    if (item.entityType === 'job') {
      eventBus.emit('job:mutated', { id: item.entityId });
      return;
    }

    if (item.entityType === 'inspection') {
      const jobId = item.endpoint.match(/^\/jobs\/([^/]+)/)?.[1];
      eventBus.emit('inspection:mutated', { id: item.entityId, jobId });
      return;
    }

    if (item.entityType === 'report') {
      const jobId = item.endpoint.match(/^\/jobs\/([^/]+)/)?.[1];
      eventBus.emit('report:mutated', { id: item.entityId, jobId });
    }
  }

  private emitMediaMutationEvent(item: MediaQueueItem): void {
    const matches = item.uploadEndpoint.match(/^\/jobs\/([^/]+)(?:\/inspections\/([^/]+))?/);
    const jobId = matches?.[1];
    const inspectionId = matches?.[2];

    if (inspectionId) {
      eventBus.emit('inspection:mutated', { id: inspectionId, jobId });
      return;
    }

    if (jobId) {
      eventBus.emit('job:mutated', { id: jobId });
    }
  }
}

/** Singleton sync engine instance */
export const syncEngine = new SyncEngine();
